import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, HostListener, inject, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NavGroup, NavigationService } from './navigation.service';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenav } from '@angular/material/sidenav';
import { RegisterComponent } from '../../modules/auth/register/register.component';
import { MatDialog } from '@angular/material/dialog';
import { LoginComponent } from '../../modules/auth/login/login.component';
import { AuthService } from '../../core/services/auth.service';
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { UserNotification } from '../../models/user-profile.model';
import { environment } from '../../../environments/environment';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { UsersApiService } from '../../api/users.service';
import { TimeAgoPipe } from "../../core/pipes/timeAgo.pipe";
// import { RewardsDialogService } from '../../core/services/rewards.dialog.service';
import { provideIcons, NgIcon } from '@ng-icons/core';
import { heroAcademicCap, heroArchiveBox, heroBellAlert, heroBriefcase, heroChatBubbleOvalLeft, heroChevronDown, heroCurrencyDollar, heroEnvelopeOpen, heroHandRaised, heroLockOpen, heroMicrophone, heroMoon, heroRocketLaunch, heroSquares2x2, heroSun, heroTrophy, heroUserPlus, heroUsers, heroVideoCamera } from '@ng-icons/heroicons/outline';
import { SocketService } from '../../core/services/socket.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UiStateService } from '../service/ui-state.service';
import { NavigationEnd } from '@angular/router';

interface NotificationResponse {
  unread: number;
  notificationCount: number;
  notifications: any[];
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatButtonModule, MatMenuModule, MatTooltipModule, UserAvatarComponent, ReactiveFormsModule, TimeAgoPipe, NgIcon],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  providers: [provideIcons({
    heroBellAlert,
    heroChatBubbleOvalLeft,
    heroUsers,
    heroTrophy,
    heroUserPlus,
    heroCurrencyDollar,
    heroAcademicCap,
    heroArchiveBox,
    heroRocketLaunch,
    heroBriefcase,
    heroEnvelopeOpen,
    heroVideoCamera,
    heroSquares2x2,
    heroChevronDown,
    heroMicrophone,
    heroLockOpen,
    heroHandRaised,
    heroSun,
    heroMoon
  })]
})
export class NavbarComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  hideNavbar = false;
  @Input() sidenav!: MatSidenav;
  @ViewChild(MatMenuTrigger) searchMenuTrigger!: MatMenuTrigger;
  profileApiUrl = environment.profileApiUrl;
  user$: any;
  themeIcon: string = 'heroSun';
  themeText: string = 'Light Mode';
  isBrowser: boolean;
  navData: { routeLink: string; icon: string; label: string; visible: boolean; }[] = [];
  navGroups: NavGroup[] = [];
  private destroy$ = new Subject<void>();
  notifications: UserNotification[] = [];
  badges: any = [];
  count: number = 0;
  notificationCount: number = 0;
  currentPage = 1;
  isLoading = false;
  hasMoreNotifications = true;
  searchControl = new FormControl('');
  totalUnreadMessageCount = 0;
  private subscription: Subscription = new Subscription();
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private navigationService: NavigationService,
    private router: Router,
    public authService: AuthService,
    public apiService: UsersApiService,
    // private rewardsDialogService: RewardsDialogService,
    private _socketService: SocketService,
    private snackBar: MatSnackBar,
    private uiState: UiStateService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.navData = this.navigationService.navData;
    this.navGroups = this.navigationService.navGroups;
  }
  ngOnInit(): void {
    this.user$ = this.authService.user$;
    this.uiState.hideNavbar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hide => this.hideNavbar = hide);
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd && !event.urlAfterRedirects.startsWith('/jobs')) {
          this.uiState.setHideNavbar(false);
        }
      });
    if (this.isBrowser) {
      this.setInitialTheme();
      this._socketService.totalUnreadCount$
        .pipe(takeUntil(this.destroy$))
        .subscribe(count => {
          this.totalUnreadMessageCount = count;
        });

      this.authService.notifications$
        .pipe(takeUntil(this.destroy$))
        .subscribe((notification: NotificationResponse) => {
          if (notification) {
            this.count = notification.unread || 0;
            this.notificationCount = notification.notificationCount || 0;
            this.notifications = notification.notifications || [];
          }
        });
      this._socketService.connectionRequest$
        .pipe(takeUntil(this.destroy$))
        .subscribe(data => {
          if (data) {
            this.handleConnectionRequestNotification(data);
          }
        });

      this._socketService.connectionAccepted$
        .pipe(takeUntil(this.destroy$))
        .subscribe(data => {
          if (data) {
            this.handleConnectionAcceptedNotification(data);
          }
        });

      this._socketService.connectionRejected$
        .pipe(takeUntil(this.destroy$))
        .subscribe(data => {
          if (data) {
            this.handleConnectionRejectedNotification(data);
          }
        });
    }
  }
  private handleConnectionRequestNotification(data: any) {
    this.snackBar.open(
      `${data.name || 'Someone'} wants to connect with you`,
      'View',
      {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['connection-request-snackbar']
      }
    );

    // Refresh notifications to show the new request
    this.refreshNotifications();
  }

  private handleConnectionAcceptedNotification(data: any) {
    this.snackBar.open(
      `${data.name || 'Someone'} accepted your connection request`,
      'Close',
      {
        duration: 4000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['connection-accepted-snackbar']
      }
    );
  }

  private handleConnectionRejectedNotification(data: any) {
    this.snackBar.open(
      `${data.name || 'Someone'} declined your connection request`,
      'Close',
      {
        duration: 4000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['connection-rejected-snackbar']
      }
    );
  }

  private refreshNotifications() {
    this.authService.getNotifications(1).subscribe({
      next: (response) => {
        // Update notifications will be handled by the existing subscription
      },
      error: (error) => {
        console.error('Error refreshing notifications:', error);
      }
    });
  }


  closeMenu() {
    this.searchMenuTrigger.closeMenu();
  }

  openMenu() {
    this.searchMenuTrigger.openMenu();
  }

  logout() {
    this.authService.logout();
  }

  getProfileImageUrl(profilePictureKey: string): string {
    if (!profilePictureKey) return '';
    return `${this.profileApiUrl}/${profilePictureKey}`;
  }

  navigateTo(routes: any, id?: string): void {
    this.router.navigate([routes], { queryParams: { user: id } });
  }

  handleJobsClick(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/jobs'], { queryParams: { view: 'all' } });
    } else {
      this.openLoginDialog();
    }
  }


  navigateToSettings(routes: any, user: string, section: string): void {
    this.router.navigate([routes], { queryParams: { user: user, section: section } });
  }

  onSearch() {
    const searchTerm = this.searchControl.value;
    if (searchTerm) {
      this.router.navigate(['search'], { queryParams: { q: searchTerm } });
    }
  }

  setInitialTheme(): void {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const html = document.documentElement;
    if (savedTheme == 'tw-dark') {
      html.classList.add('tw-dark');
      localStorage.setItem('theme', 'tw-dark');
      this.themeIcon = 'heroMoon';
      this.themeText = 'Dark Mode';
    } else {
      html.classList.remove('tw-dark');
      localStorage.setItem('theme', 'light');
      this.themeIcon = 'heroSun';
      this.themeText = 'Light Mode';
    }
  }

  toggleTheme(): void {
    if (!this.isBrowser) return;
    const html = document.documentElement;
    if (html.classList.contains('tw-dark')) {
      html.classList.remove('tw-dark');
      localStorage.setItem('theme', 'light');
      this.themeIcon = 'heroSun';
      this.themeText = 'Light Mode';
    } else {
      html.classList.add('tw-dark');
      localStorage.setItem('theme', 'tw-dark');
      this.themeIcon = 'heroMoon';
      this.themeText = 'Dark Mode';
    }
  }

  @HostListener('window:DOMContentLoaded')
  onDOMContentLoaded(): void {
    if (this.isBrowser) {
      this.setInitialTheme();
    }
  }
  toggleSidenav() {
    this.sidenav.toggle();
  }

  openSignupDialog(): void {
    const dialogRef = this.dialog.open(RegisterComponent, {
      minHeight: '600px',
      maxHeight: '98vh',
      width: '500px',
      maxWidth: '96vw',
      disableClose: false,
      exitAnimationDuration: '500ms',
      enterAnimationDuration: '300ms',
      data: []
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
      }
    });
  }

  openLoginDialog(): void {
    const dialogRef = this.dialog.open(LoginComponent, {
      maxHeight: '98vh',
      width: '350px',
      maxWidth: '96vw',
      disableClose: false,
      exitAnimationDuration: '500ms',
      enterAnimationDuration: '300ms',
      data: []
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
      }
    });
  }

  handleNotificationClick(notification: any): void {
    const { notificationType, code, postTitle, commentReply, followerText, _id } = notification;

    // Utility to slugify titles
    const slugify = (text: string) =>
      text?.replace(/[^A-Z0-9]+/ig, "_").toLowerCase() || '';

    const postSlug = slugify(postTitle);
    const followerSlug = slugify(followerText);

    let path: any[] = [];
    let queryParams: any = {};

    switch (notificationType) {
      case "connection-request":
        path = ['/messages'];
        queryParams.viewRequests = 'all';
        break;

      case "connection-accepted":
        path = ['/messages', (notification.name.toLowerCase()
          .replace(/\s+/g, ''))];
        break;

      case "connection-rejected":
        queryParams.viewRequests = 'rejected';
        path = ['/messages'];
        break;

      // Posts and related replies
      case "post-reply":
      case "reported-post":
      case "poll-vote":
        path = ['post', code, postSlug];
        break;

      case "comment-reply":
        path = ['post', code, postSlug];
        queryParams.comment = commentReply;
        break;

      // Announcement
      case "announcement-post-reply":
        path = ['announcement', code, postSlug];
        break;

      case "announcement-comment-reply":
        path = ['announcement', code, postSlug];
        queryParams.comment = commentReply;
        break;

      // Quiz
      case "quiz-post-reply":
        path = ['quiz', code, postSlug];
        break;

      case "quiz-comment-reply":
        path = ['quiz', code, postSlug];
        queryParams.comment = commentReply;
        break;

      // Hustle
      case "hustle-post-reply":
        path = ['hustle', code, postSlug];
        break;

      case "hustle-comment-reply":
        path = ['hustle', code, postSlug];
        queryParams.comment = commentReply;
        break;

      // Accident
      case "accident-post-reply":
        path = ['accident', code, postSlug];
        break;

      case "accident-comment-reply":
        path = ['accident', code, postSlug];
        queryParams.comment = commentReply;
        break;

      // Milestone
      case "milestone-post-reply":
        path = ['milestone', code, postSlug];
        break;

      case "milestone-comment-reply":
        path = ['milestone', code, postSlug];
        queryParams.comment = commentReply;
        break;

      // Likes (redirect to appropriate post types)
      case "post-like":
        path = ['post', code, postSlug];
        break;

      case "milestone-like":
        path = ['milestone', code, postSlug];
        break;

      case "announcement-like":
        path = ['announcement', code, postSlug];
        break;

      case "quiz-like":
        path = ['quiz', code, postSlug];
        break;

      case "accident-like":
        path = ['accident', code, postSlug];
        break;

      case "hustle-like":
        path = ['hustle', code, postSlug];
        break;

      // Profile-based notifications
      case "new-follower":
      case "reported-user":
        path = ['/profile'];
        queryParams.user = code;
        queryParams.title = followerSlug;
        break;

      default:
        console.warn("Unknown notification type:", notificationType);
        return;
    }


    this.router.navigate(path, { queryParams });

    this.markNotificationAsRead(_id);
  }

  getNotificationIcon(notify: any): string {
    switch (notify.notificationType) {
      case 'connection-request':
        return 'user-plus';
      case 'connection-accepted':
        return 'user-check';
      case 'connection-rejected':
        return 'user-minus';

      case 'post-reply':
      case 'announcement-post-reply':
        return 'message';

      case 'comment-reply':
      case 'announcement-comment-reply':
        return 'answer';

      case 'new-follower':
        return 'user';

      case 'reported-user':
      case 'reported-post':
        return 'warning';

      case 'post-like':
      case 'milestone-like':
      case 'announcement-like':
      case 'quiz-like':
      case 'accident-like':
      case 'hustle-like':
        return 'like';
      case 'email-verification':
        return 'email';

      default:
        return 'message';
    }
  }

  handleScroll(event: Event) {
    const element = event.target as HTMLElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (atBottom && !this.isLoading && this.hasMoreNotifications && this.notificationCount !== this.notifications.length) {
      this.loadMoreNotifications();
    }
  }


  loadMoreNotifications(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    setTimeout(() => {
      this.currentPage++;

      this.authService.getNotifications(this.currentPage).subscribe({
        next: (res) => {
          if (res.notifications.notifications.length === 0) {
            this.hasMoreNotifications = false;
          } else {
            // this.notifications = res.notifications.notifications;
          }
          this.isLoading = false;

        },
        error: (err) => {
          console.error('Error loading more notifications', err);
          this.isLoading = false;
          this.currentPage--;
        }
      });
    }, 2000);
  }

  markAllAsRead() {
    this.apiService.markAllNotificationAsRead().subscribe({
      next: async (response: any) => {
        this._socketService.clearAllNotifications();
        this.count = 0;
        this.notifications = this.notifications.map(n => ({ ...n, unread: false }));
        this.authService.getNotifications(1).subscribe(res => { });
      },
      error: (error: any) => {
        console.error('Error marking all as read:', error);
      },
    });
  }

  deleteNotification(id: string) {
    this.apiService.deleteNotification(id).subscribe({
      next: async (response: any) => {
        this.notifications = this.notifications.filter(n => n._id !== id);
        this.count = Math.max(0, this.count - 1);
        this.notificationCount = Math.max(0, this.notificationCount - 1);

        this.authService.getNotifications(1).subscribe(res => { });
      },
      error: (error: any) => {
        console.error('Error deleting notification:', error);
      },
    });
  }

  markNotificationAsRead(id: string) {
    this.apiService.markNotificationAsRead(id).subscribe({
      next: async (response: any) => {
        // Update local notification
        this.notifications = this.notifications.map(n =>
          n._id === id ? { ...n, unread: false } : n
        );
        this.count = Math.max(0, this.count - 1);

        this._socketService.markNotificationAsRead(id);
        this.authService.getNotifications(1).subscribe(res => { });
      },
      error: (error: any) => {
        console.error('Error marking notification as read:', error);
      },
    });
  }

  getNotificationIconBackground(notificationType: string): string {
    switch (notificationType) {
      case 'connection-request':
      case 'connection-accepted':
        return 'tw-bg-green-100 dark:tw-bg-green-900/20';

      case 'connection-rejected':
        return 'tw-bg-red-100 dark:tw-bg-red-900/20';
      case 'post-like':
      case 'milestone-like':
      case 'announcement-like':
      case 'quiz-like':
      case 'accident-like':
      case 'hustle-like':
        return 'tw-bg-pink-100 dark:tw-bg-pink-900/20';
      case 'reported-user':
      case 'reported-post':
        return 'tw-bg-red-100 dark:tw-bg-red-900/20';
      default:
        return 'tw-bg-blue-100 dark:tw-bg-blue-900/20';
    }
  }

  getNotificationText(notification: any): string {
    switch (notification.notificationType) {
      case 'connection-request':
        return `${notification.name || 'Someone'} wants to connect with you`;
      case 'connection-accepted':
        return `${notification.name || 'Someone'} accepted your connection request`;
      case 'connection-rejected':
        return `${notification.name || 'Someone'} declined your connection request`;
      default:
        return notification.text || notification.followerText || 'New notification';
    }
  }

  isConnectionRequest(notificationType: string): boolean {
    return ['connection-request', 'connection-accepted', 'connection-rejected'].includes(notificationType);
  }

  getUserInitials(name: string): string {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || '??';
  }

  deleteAllNotifications() {
    for (const notification of this.notifications) {
      this.deleteNotification(notification._id)
    }
    this.notificationCount = 0
  };

  // openRewards() {
  //   this.rewardsDialogService.openDialogManually();
  // };

  clearAllNotifications() {
    this._socketService.clearAllNotifications();
    this.notifications = [];
    this.count = 0;
    this.notificationCount = 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  // -----------------------end---------------------------------------
}
